import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule } from '@ngx-translate/core';
import { InputHelperTextComponent } from 'app/shared/components/input-helper-text/input-helper-text.component';
import { HelperService } from 'app/shared/services/helper.service';
import { Subject, takeUntil } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-select-form-field',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, NgSelectModule, InputHelperTextComponent, NgbTooltipModule],
  templateUrl: './select-form-field.component.html',
  styleUrl: './select-form-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectFormFieldComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SelectFormFieldComponent),
      multi: true,
    }
  ],
})
export class SelectFormFieldComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() label: string = '';
  @Input() type: string = 'text';
  @Input() placeholder: string = 'placeholders.please_select';
  @Input() isRequired: boolean = false;
  @Input() infoText: string = '';
  @Input() clearable: boolean = false;
  @Input() searchable: boolean = false;
  @Input() bindLabel: string = 'name';
  @Input() bindValue: string = 'id';
  @Input() items: any = [];
  @Input() multipleSelect: boolean = false;
  @Input() users: boolean = true;
  @Input() viewMode!: boolean;
  @Input() fullWidth!: boolean;
  @Input() customClass: string = '';
  @Input() externalSearch: boolean = false;
  @Input() isLabelTopPosition: boolean = false;
  @Input() helpText!: string;
  @Input() takeCount: number = 50;
  @Input() tooltip: boolean = true;
  @Output() onSelectEvent = new EventEmitter<any>();
  @Output() onSearchEvent = new EventEmitter<any>();
  @Output() onClearEvent = new EventEmitter<boolean>();
  @Output() onOpenEvent = new EventEmitter<void>();
  @Output() onScrollEvent = new EventEmitter<any>();

  page: number = 0;
  term: string = '';

  formControl: FormControl = new FormControl<string>('');
  errorText: string = '';
  inlineEditMode!: boolean;

  searchInput$ = new Subject<string>();

  private unsubscribe$ = new Subject<void>()

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private helperService: HelperService
  ) {
    this.searchInput$
      .pipe(
        debounceTime(300),
      )
      .subscribe(() => {
        this.page = 0;
        this.onSearchEvent.emit({ page: this.page, term: this.term, take: this.takeCount });
      });
  }

  onTouch: () => void = () => { };
  onChange: (value: any) => void = () => { };

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.formControl.disable() : this.formControl.enable();
  }

  writeValue(value: string): void {
    this.formControl.setValue(value, { emitEvent: false });

    this.changeDetectorRef.markForCheck();
  }

  ngOnInit(): void {
    this.formControl.valueChanges
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe((value) => {
        this.onChange(value);
      });
  }

  returnTrue(): boolean {
    return true;
  }

  validate(control: FormControl) {
    setTimeout(() => {
      this.errorText = this.helperService.handleFieldsErrors(control);
      this.changeDetectorRef.markForCheck();
    });
    return null;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onSearch($event: any) {
    this.term = $event.term;
    if ($event.term.length === 0 || $event.term.length > 2 && this.externalSearch) {
      this.searchInput$.next($event.term);
    }
  }

  onScrollToEnd(): void {
    this.page++
    this.onScrollEvent.emit({ page: this.page, term: this.term, take: this.takeCount });
  }

  change(data: any) {
    this.onSelectEvent.emit(data);
  }

  onClear() {
    this.page = 0;
    this.onClearEvent.emit(true);
  }

  onOpen() {
    this.term = '';
    this.onOpenEvent.emit();
  }

}
